import { useAuthStore } from '~/stores/useAuthStore'
import { useUserDataStore } from '~/stores/useUserDataStore'
import { LOCALES_BY_CODE } from '../locales'
import { languageRedirect } from '../utils/redirect'

const LANG_STORAGE_KEY = 'pitchup-language-v2'

export default defineNuxtPlugin(async (nuxtApp) => {
  // Skip language plugin during error pages to prevent cascading failures
  if (nuxtApp.payload.error) {
    return
  }

  const userDataStore = useUserDataStore(usePinia())
  const authStore = useAuthStore(usePinia())
  const i18n = nuxtApp.$i18n as { locale: string }

  // Check if the user agent is a bot
  const isBot = () => {
    if (!process.client) return true // Consider server-side as bot traffic
    const userAgent = navigator.userAgent.toLowerCase()
    // Common bot patterns
    const botPatterns = [
      'bot',
      'crawler',
      'spider',
      'slurp',
      'facebookexternalhit',
      'whatsapp',
    ]
    return botPatterns.some((pattern) => userAgent.includes(pattern))
  }

  // Skip all language handling for bots
  if (isBot()) {
    return
  }

  // Wait for auth check to complete if user might be logged in
  if (process.client) {
    await authStore.authCheck
  }

  // Step 1: Handle legacy language migration from localStorage
  const migrateLegacyLanguage = async () => {
    const legacyLangStorage = localStorage.getItem(LANG_STORAGE_KEY)
    if (legacyLangStorage) {
      // Clean any potential quotes from the language code - ONLY needed for legacy storage
      const cleanLangCode = legacyLangStorage.replace(/^["'](.+)["']$/, '$1')

      try {
        // Check if it's the special 'user-cancelled' value
        if (cleanLangCode === 'user-cancelled') {
          // Legacy: user-cancelled meant they didn't want redirects, but we're removing this feature
          // Just use current locale as the language preference
          await userDataStore.setLanguage(i18n.locale)
        } else {
          // Validate the language code before using it
          if (LOCALES_BY_CODE[cleanLangCode]) {
            await userDataStore.setLanguage(cleanLangCode)
          } else {
            console.error(
              `Invalid language code in localStorage: ${cleanLangCode}`,
            )
            await userDataStore.setLanguage(i18n.locale)
          }
        }
      } catch (error) {
        // Log to Sentry but don't throw - migration is not critical
        console.error(
          'Failed to migrate legacy language (non-critical):',
          error,
        )
        if (nuxtApp.$sentry) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          nuxtApp.$sentry.captureException(error, {
            level: 'warning',
            tags: { context: 'language-migration' },
          })
        }
      }
      localStorage.removeItem(LANG_STORAGE_KEY)
    }
  }

  // Step 2: Validate the language stored in user preferences
  const validateLanguage = (language: string | undefined): boolean => {
    if (!language) return false

    if (!LOCALES_BY_CODE[language]) {
      console.error(
        `Invalid language code: ${language}`,
        Object.keys(LOCALES_BY_CODE),
      )
      return false
    }

    return true
  }

  // Step 3: Initialize language if not set
  const initializeLanguage = async (language: string | undefined) => {
    // If language is missing or will be invalid after cleaning, use current locale
    if (!language) {
      try {
        await userDataStore.setLanguage(i18n.locale)
      } catch (error) {
        // Log to Sentry but don't throw - language preference is not critical
        console.error(
          'Failed to save language preference (non-critical):',
          error,
        )
        if (nuxtApp.$sentry) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          nuxtApp.$sentry.captureException(error, {
            level: 'warning',
            tags: { context: 'language-init' },
          })
        }
      }
      return i18n.locale
    }

    // Check if it's valid
    if (!LOCALES_BY_CODE[language]) {
      try {
        await userDataStore.setLanguage(i18n.locale)
      } catch (error) {
        // Log to Sentry but don't throw - language preference is not critical
        console.error(
          'Failed to save language preference (non-critical):',
          error,
        )
        if (nuxtApp.$sentry) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          nuxtApp.$sentry.captureException(error, {
            level: 'warning',
            tags: { context: 'language-validation' },
          })
        }
      }
      return i18n.locale
    }

    // Return the valid language code
    return language
  }

  // Step 4: Handle redirection if needed
  const handleLanguageRedirect = (language: string) => {
    if (language !== i18n.locale) {
      languageRedirect(useRoute().fullPath, language)
    }
  }

  // Function to check and apply language settings
  const checkAndApplyLanguage = async () => {
    let currentLanguage = userDataStore.globalSettings?.language

    // If language is invalid or needs cleaning, initialize it properly
    if (!currentLanguage || !validateLanguage(currentLanguage)) {
      currentLanguage = await initializeLanguage(currentLanguage)
    }

    // At this point, currentLanguage should always be valid
    // Double-check before redirecting
    if (currentLanguage && LOCALES_BY_CODE[currentLanguage]) {
      handleLanguageRedirect(currentLanguage)
    } else {
      console.error(
        'Unexpected invalid language after processing:',
        currentLanguage,
      )
    }
  }

  // Clean up legacy redirect cancellation flag
  const cleanupLegacyFlags = () => {
    // Remove the old redirect cancellation flag if it exists
    if (localStorage.getItem('pitchup-language-redirect-cancelled')) {
      localStorage.removeItem('pitchup-language-redirect-cancelled')
    }
  }

  // Main execution flow
  cleanupLegacyFlags()
  await migrateLegacyLanguage()

  // If user is logged in, wait a bit for user data to load
  // This helps prevent race conditions where we redirect before user settings are loaded
  if (authStore.isLoggedIn && !userDataStore.globalSettings?.language) {
    // Give the user data store time to load settings
    await new Promise((resolve) => {
      const unwatch = watch(
        () => userDataStore.globalSettings?.language,
        (newLang) => {
          if (newLang) {
            unwatch()
            resolve(true)
          }
        },
      )
      // Timeout after 500ms to prevent hanging
      setTimeout(() => {
        unwatch()
        resolve(false)
      }, 500)
    })
  }

  await checkAndApplyLanguage()

  // Watch for changes to language preferences (e.g., after login or manual switch)
  if (process.client) {
    // Watch the actual language value in the store instead of just the signal
    watch(
      () => userDataStore.globalSettings?.language,
      (newLang, _oldLang) => {
        if (!newLang) return

        if (newLang !== i18n.locale) {
          if (LOCALES_BY_CODE[newLang]) {
            handleLanguageRedirect(newLang)
          } else {
            console.error(`Invalid language in watcher: ${newLang}`)
          }
        }
      },
    )

    // Also keep the signal watcher as a backup
    watch(
      () => userDataStore.languageChanged,
      async () => {
        await checkAndApplyLanguage()
      },
    )
  }
})
